import { getColorValue, getContrastColor } from './colorUtils';

/**
 * Generates CSS custom property values for the booking page theme.
 * Set the returned object as inline style on PublicBookingLayout's root div.
 * All descendant components access these values via var(--theme-*).
 *
 * @param {string} primaryColor — Tailwind color name, e.g. 'blue-600'
 * @param {string} themeMode — 'light' or 'dark'
 * @returns {Object} CSS custom properties to spread onto style={{}}
 */
export const getThemeCSSVars = (primaryColor = 'blue-600', themeMode = 'light') => {
  const isDark = themeMode === 'dark';
  const primaryHex = getColorValue(primaryColor);
  const contrastColor = getContrastColor(primaryHex);

  // Derive shade variants
  const primaryHoverHex = darkenByPercent(primaryHex, 10);
  const primaryLightHex = setLightnessMaxSat(primaryHex, 88, 45);
  const primaryMediumHex = setLightnessMaxSat(primaryHex, 70, 50);
  const primaryDarkHex = setLightness(primaryHex, 25);
  const primaryVeryLightHex = setLightnessMaxSat(primaryHex, 93, 40);

  return {
    // ─── Primary color and variants ───
    '--theme-primary': primaryHex,
    '--theme-primary-hover': primaryHoverHex,
    '--theme-primary-contrast': contrastColor === 'white' ? '#ffffff' : '#000000',

    // Selected state: calendar days, time slots, radio indicators
    // Light mode: light bg, dark text, medium border
    // Dark mode: darker bg, light text, primary border
    '--theme-selected-bg': isDark ? primaryHoverHex : primaryLightHex,
    '--theme-selected-text': isDark ? primaryLightHex : primaryDarkHex,
    '--theme-selected-border': isDark ? primaryHex : primaryMediumHex,

    // Today indicator (calendar)
    '--theme-today-bg': isDark ? '#374151' : primaryVeryLightHex,

    // Subtle tint for hover effects (10% opacity in light, 20% in dark to
    // remain perceptible on the darker card background).
    '--theme-primary-tint': isDark ? `${primaryHex}33` : `${primaryHex}1A`,

    // Quiet surface variant — desaturated wash for badges / secondary fills
    '--theme-primary-soft': setLightnessMaxSat(primaryHex, 80, 30),

    // Badge colors (service location badges)
    '--theme-badge-bg': isDark ? '#4b5563' : '#ffffff',
    '--theme-badge-text': isDark ? '#ffffff' : primaryDarkHex,
    '--theme-badge-border': isDark ? '#6b7280' : primaryMediumHex,

    // ─── Page-level colors ───
    '--theme-bg-page': isDark ? '#1f2937' : '#f9fafb',
    '--theme-bg-card': isDark ? '#374151' : '#ffffff',
    '--theme-bg-card-elevated': isDark ? '#4b5563' : '#ffffff',
    '--theme-text-primary': isDark ? '#f9fafb' : '#111827',
    '--theme-text-secondary': isDark ? '#d1d5db' : '#4b5563',
    '--theme-border': isDark ? '#4b5563' : '#d1d5db',

    // ─── Unavailable slot state (time-slot grid) ───
    // These must RECEDE relative to --theme-bg-card in both modes: an
    // unbookable slot should sink into the page, never advance out of it.
    // Do not substitute --theme-bg-card-elevated here — that is an *elevation*
    // token and is deliberately lighter than the card, which in dark mode makes
    // a disabled slot read as more prominent than an available one.
    '--theme-slot-disabled-bg': isDark ? '#2b3440' : '#f1f0ee',
    // Muted, but still ≥4.5:1 against --theme-slot-disabled-bg above
    // (light 4.71:1, dark 4.75:1) — an unbookable time must stay readable.
    '--theme-slot-disabled-text': isDark ? '#94a0b0' : '#6f6a63',
    // Near-invisible: the disabled slot reads as a flat well, not a card.
    '--theme-slot-disabled-border': isDark ? '#2b3440' : '#e7e5e2',

    // ─── Focus ring ───
    '--theme-focus-ring': `${primaryHex}80`,

    // ─── Secondary button (outline variant) ───
    '--theme-secondary-text': isDark ? '#ffffff' : primaryHex,
    '--theme-secondary-bg': isDark ? `${primaryHex}20` : '#ffffff',
    '--theme-secondary-bg-hover': isDark ? `${primaryHex}30` : `${primaryHex}1A`,
  };
};

/**
 * Returns whether the theme is dark mode. Utility for components
 * that need boolean checks (e.g., conditional rendering logic).
 */
export const isDarkMode = (themeMode) => themeMode === 'dark';

/**
 * Tailwind class bundle that components (Modal, SearchLookup, etc.)
 * use to render against the booking-page theme without hardcoding
 * dark/light branches at every call site.
 */
export const getThemedBg = (themeMode, primaryColor = null) => {
  const isDark = isDarkMode(themeMode);
  return {
    isDark,
    // Dark mode uses the themed CSS-var utility classes (.bg-themed-card etc.,
    // see booking-theme.css) so modal surfaces match the booking page's actual
    // cool-gray dark palette rather than the warm Quiet Editorial ink ramp.
    // These resolve correctly inside Headless UI's body-portaled Dialog because
    // `style` (below) re-injects the --theme-* vars onto the DialogPanel.
    cardBackground: isDark ? 'bg-themed-card' : 'bg-white',
    textPrimary: isDark ? 'text-themed-primary' : 'text-ink-900',
    textSecondary: isDark ? 'text-themed-secondary' : 'text-ink-500',
    // `border` is a ring-* token (for ring-1 utilities). `borderClass`
    // is a border-* token for actual border-{t,r,b,l} usage — the two
    // are not interchangeable in Tailwind.
    border: isDark ? 'ring-themed-border' : 'ring-black/5',
    borderClass: isDark ? 'border-themed' : 'border-ink-100',
    // CSS custom-property map for re-injecting the theme onto a portaled
    // element (e.g. Modal's DialogPanel). Note the arg order differs from
    // getThemedBg: getThemeCSSVars(primaryColor, themeMode).
    style: getThemeCSSVars(primaryColor || undefined, themeMode),
    primaryColor,
  };
};

/**
 * Extracts theme config from bookingPage object. Preserved from
 * the original themeUtils.js for backward compatibility.
 */
export const getBookingPageTheme = (bookingPage) => ({
  primaryColor: bookingPage?.primaryColor || 'blue-600',
  themeMode: bookingPage?.themeMode || 'light',
});

function hexToHsl(hex) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = ((num >> 16) & 0xff) / 255;
  const g = ((num >> 8) & 0xff) / 255;
  const b = (num & 0xff) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r)      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / delta + 2) / 6;
    else                h = ((r - g) / delta + 4) / 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h, s, l) {
  const sn = s / 100, ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const toChannel = (n) => {
    const k = (n + h / 30) % 12;
    const val = ln - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(val * 255).toString(16).padStart(2, '0');
  };
  return `#${toChannel(0)}${toChannel(8)}${toChannel(4)}`;
}

function setLightness(hex, targetL, saturationScale = 1.0) {
  const [h, s] = hexToHsl(hex);
  return hslToHex(h, Math.min(100, Math.round(s * saturationScale)), targetL);
}

function setLightnessMaxSat(hex, targetL, maxS) {
  const [h, s] = hexToHsl(hex);
  return hslToHex(h, Math.min(s, maxS), targetL);
}

function darkenByPercent(hex, amount, minL = 20) {
  const [h, s, l] = hexToHsl(hex);
  return hslToHex(h, s, Math.max(minL, l - amount));
}
